import React from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    required,
    useTranslate,
    useNotify,
    useRedirect,
    Toolbar,
    SaveButton,
    DeleteButton,
    useEditController,
} from 'react-admin';
import { Box, Card, CardContent, Typography, IconButton } from '@mui/material';
import { ArrowBack, CarRental } from '@mui/icons-material';

const transformModelSubmission = (data: any) => {
    // Only send translations the user actually populated. Sending empty strings
    // would be silently dropped on the backend; instead the backend now rejects
    // empty values explicitly, so we only forward truthy fields. The form's
    // required validator on translation_en still guarantees English is set.
    const translations: Record<string, string> = {};
    if (typeof data.translation_en === 'string' && data.translation_en.trim()) {
        translations.en = data.translation_en;
    }
    if (typeof data.translation_zh_tw === 'string' && data.translation_zh_tw.trim()) {
        translations['zh-TW'] = data.translation_zh_tw;
    }
    if (typeof data.translation_zh_cn === 'string' && data.translation_zh_cn.trim()) {
        translations['zh-CN'] = data.translation_zh_cn;
    }
    return { translations };
};

const EditToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            display: 'flex',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: { xs: 2, sm: 4 },
        }}
    >
        <SaveButton
            variant="contained"
            sx={{
                backgroundColor: '#00C853',
                '&:hover': { backgroundColor: '#00A843' },
            }}
        />
        <DeleteButton />
    </Toolbar>
);

const PageHeader = () => {
    const translate = useTranslate();
    const redirect = useRedirect();
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: { xs: 2, sm: 4 } }}>
            <IconButton
                onClick={() => redirect('/vehicle-models')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
                title={translate('ra.action.back')}
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.vehicle-models.edit.title', { _: 'Edit Vehicle Model' })}
                </Typography>
            </Box>
        </Box>
    );
};

const FlattenRecord: React.FC = () => {
    const { record } = useEditController();
    if (!record) return null;
    const flattened = {
        ...record,
        translation_en: (record.translations as any)?.en || '',
        translation_zh_tw: (record.translations as any)?.['zh-TW'] || '',
        translation_zh_cn: (record.translations as any)?.['zh-CN'] || '',
    };
    return <FormBody record={flattened} />;
};

const FormBody: React.FC<{ record: any }> = ({ record }) => {
    const translate = useTranslate();
    return (
        <SimpleForm
            toolbar={<EditToolbar />}
            record={record}
            defaultValues={record}
            sx={{
                '& .MuiPaper-root': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
                '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                    px: { xs: 0, sm: 2 },
                    py: { xs: 1, sm: 2 },
                },
                padding: { xs: 0, sm: 'inherit' },
            }}
        >
            <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                <PageHeader />
                <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <CarRental sx={{ mr: 2, color: '#00C853' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {translate('resources.vehicle-models.sections.details', {
                                    _: 'Model details',
                                })}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <TextInput
                                source="brand_name"
                                label={translate('resources.vehicle-models.fields.brand', { _: 'Brand' })}
                                disabled
                                fullWidth
                                helperText={translate('resources.vehicle-models.help.brandImmutable', {
                                    _: 'Brand cannot be changed after creation. Delete and recreate to move a model.',
                                })}
                            />
                            <TextInput
                                source="normalized_name"
                                label={translate('resources.vehicle-models.fields.normalizedName', {
                                    _: 'Normalized Name',
                                })}
                                disabled
                                fullWidth
                            />
                            <TextInput
                                source="translation_en"
                                label={translate('resources.vehicle-models.fields.translationEn', {
                                    _: 'English Name',
                                })}
                                validate={required()}
                                fullWidth
                            />
                            <TextInput
                                source="translation_zh_tw"
                                label={translate('resources.vehicle-models.fields.translationZhTw', {
                                    _: '繁體中文 Name',
                                })}
                                fullWidth
                            />
                            <TextInput
                                source="translation_zh_cn"
                                label={translate('resources.vehicle-models.fields.translationZhCn', {
                                    _: '简体中文 Name',
                                })}
                                fullWidth
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </SimpleForm>
    );
};

export const VehicleModelsEdit = () => {
    const notify = useNotify();
    const redirect = useRedirect();

    return (
        <Edit
            title={false}
            component="div"
            mutationMode="pessimistic"
            transform={transformModelSubmission}
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
            mutationOptions={{
                onSuccess: () => {
                    notify('resources.vehicle-models.notifications.updated', {
                        type: 'success',
                        messageArgs: { _: 'Model updated' },
                    });
                    redirect('list', 'vehicle-models');
                },
                onError: (error: any) => {
                    notify(error?.body?.error || error?.message || 'Failed to update model', {
                        type: 'error',
                    });
                },
            }}
        >
            <FlattenRecord />
        </Edit>
    );
};
